import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowsClockwise, Plus, Spinner, Trash, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/onboarding/AddressAutocomplete';
import { useConfirm } from '@/components/ConfirmDialog';
import { resolve as resolveColor } from '@/lib/teamColor';
import { addZone, updateZone, deleteZone, type Zone } from '@/api/locations';
import { ApiError } from '@/api/client';
import type { Location } from '@/api/locations';
import type { GeocodePlace } from '@/api/geocode';
import {
  LocationAvailabilityFieldset,
  type LocationAvailabilityFieldsetHandle,
} from './LocationAvailabilityFieldset';

// Submit shape sent up to the parent. The parent maps this to the
// CreateLocation / UpdateLocation API call. Zones aren't editable in the
// edit modal — manage zones via the per-zone routes (PATCH/DELETE) once a
// location exists. This keeps the create flow simple and the edit flow
// surgical (no concurrent multi-row edits to reason about).
export interface LocationFormSubmit {
  name: string;
  color: string;
  notes: string;
  place: GeocodePlace | null;
  zones: { name: string; sort_order: number; notes?: string }[];
}

interface Props {
  mode: 'create' | 'edit';
  initial?: Location;
  // slug + token are required in edit mode for live zone mutations.
  slug?: string;
  token?: string | null;
  // Pre-fill values for create mode — used when the user picks a Google
  // prediction in the locations panel: the prediction's main_text seeds
  // name, the place details seed the address. `place` may start null and
  // arrive once the parent's async details fetch resolves; the form
  // syncs it in via an effect so the modal can open instantly. Both stay
  // editable.
  seed?: { name: string; place: GeocodePlace | null };
  onSubmit: (values: LocationFormSubmit) => Promise<unknown>;
  onCancel: () => void;
  // Edit-mode destructive action. Parent owns the confirm + mutate flow;
  // the modal just renders the button. When omitted, no delete button.
  onDelete?: () => void;
  submitting: boolean;
  deleting?: boolean;
  error: string | null;
}

export function LocationFormModal({
  mode,
  initial,
  slug,
  token,
  seed,
  onSubmit,
  onCancel,
  onDelete,
  submitting,
  deleting,
  error,
}: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [name, setName] = useState(initial?.name ?? seed?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [place, setPlace] = useState<GeocodePlace | null>(
    initial && initial.formatted_address
      ? {
          place_id: initial.place_id ?? '',
          formatted_address: initial.formatted_address ?? '',
          street: initial.street ?? '',
          postal_code: initial.postal_code ?? '',
          city: initial.city ?? '',
          country_code: initial.country_code ?? '',
          latitude: initial.latitude ?? 0,
          longitude: initial.longitude ?? 0,
        }
      : (seed?.place ?? null),
  );

  // When the panel opens us before its place-details fetch resolves, seed.place
  // arrives later — sync it in unless the user already typed an address.
  useEffect(() => {
    if (seed?.place && !place) setPlace(seed.place);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.place]);
  const [manualEntry, setManualEntry] = useState(false);
  const [zoneDrafts, setZoneDrafts] = useState<{ name: string }[]>([]);
  const [colorOverride, setColorOverride] = useState(initial?.color ?? '');

  const palette = useMemo(
    () => resolveColor(name || initial?.name || '', colorOverride || null),
    [name, initial?.name, colorOverride],
  );
  const isHexValid = colorOverride === '' || /^#[0-9a-f]{6}$/i.test(colorOverride.trim());

  // ── Edit-mode zone state ──────────────────────────────────────────────
  // Zones are persisted live (each add/rename/delete hits the API immediately).
  // Local state is the source of truth for the list order; each mutation
  // syncs the relevant item on success.
  const [zones, setZones] = useState<Zone[]>(initial?.zones ?? []);
  // Per-zone uncommitted draft names (while the user is typing).
  const [zoneNames, setZoneNames] = useState<Record<string, string>>(
    () => Object.fromEntries((initial?.zones ?? []).map((z) => [z.id, z.name])),
  );
  const [zoneErrors, setZoneErrors] = useState<Record<string, string>>({});
  // Ref map to focus the input of a newly added zone.
  const zoneInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Tracks which zone mutation is in flight (for per-row spinners).
  const [pendingZone, setPendingZone] = useState<string | null>(null);

  function invalidateLocations() {
    if (slug) qc.invalidateQueries({ queryKey: ['locations', slug] });
  }

  const addZoneMut = useMutation({
    mutationFn: (input: { name: string; sort_order: number }) =>
      addZone(token ?? null, slug ?? '', initial?.id ?? '', {
        name: input.name,
        sort_order: input.sort_order,
      }),
    onSuccess: (zone) => {
      setZones((prev) => [...prev, zone]);
      setZoneNames((prev) => ({ ...prev, [zone.id]: zone.name }));
      invalidateLocations();
      // Focus the new zone's input after render.
      requestAnimationFrame(() => {
        zoneInputRefs.current[zone.id]?.focus();
      });
    },
  });

  const updateZoneMut = useMutation({
    mutationFn: (input: { zoneId: string; name: string; sort_order: number }) =>
      updateZone(token ?? null, slug ?? '', initial?.id ?? '', input.zoneId, {
        name: input.name,
        sort_order: input.sort_order,
      }),
    onSuccess: (zone) => {
      setZones((prev) => prev.map((z) => (z.id === zone.id ? zone : z)));
      setZoneErrors((prev) => { const n = { ...prev }; delete n[zone.id]; return n; });
      invalidateLocations();
    },
    onError: (err, input) => {
      const msg =
        err instanceof ApiError && err.code === 'zone_name_taken'
          ? t('locations.errors.zoneNameTaken')
          : t('locations.errors.generic');
      setZoneErrors((prev) => ({ ...prev, [input.zoneId]: msg }));
      setPendingZone(null);
    },
  });

  const deleteZoneMut = useMutation({
    mutationFn: (zoneId: string) =>
      deleteZone(token ?? null, slug ?? '', initial?.id ?? '', zoneId),
    onSuccess: (_, zoneId) => {
      setZones((prev) => prev.filter((z) => z.id !== zoneId));
      setZoneNames((prev) => { const n = { ...prev }; delete n[zoneId]; return n; });
      invalidateLocations();
    },
    onError: (err, zoneId) => {
      const msg =
        err instanceof ApiError && err.code === 'zone_in_use'
          ? t('locations.errors.zoneInUse')
          : t('locations.errors.generic');
      setZoneErrors((prev) => ({ ...prev, [zoneId]: msg }));
      setPendingZone(null);
    },
  });

  async function handleAddZone() {
    await addZoneMut.mutateAsync({ name: t('locations.zones.newZoneName'), sort_order: zones.length });
  }

  async function handleZoneBlur(zone: Zone) {
    const draft = zoneNames[zone.id] ?? zone.name;
    if (draft.trim() === zone.name || !draft.trim()) {
      // Revert to saved name if empty or unchanged.
      setZoneNames((prev) => ({ ...prev, [zone.id]: zone.name }));
      return;
    }
    setPendingZone(zone.id);
    await updateZoneMut.mutateAsync({ zoneId: zone.id, name: draft.trim(), sort_order: zone.sort_order });
    setPendingZone(null);
  }

  async function handleZoneDelete(zone: Zone) {
    const ok = await confirm({
      title: t('locations.zones.deleteConfirmTitle'),
      body: t('locations.zones.deleteConfirmBody'),
      confirmLabel: t('locations.zones.remove'),
      tone: 'destructive',
    });
    if (!ok) return;
    setPendingZone(zone.id);
    await deleteZoneMut.mutateAsync(zone.id);
    setPendingZone(null);
  }

  // ─────────────────────────────────────────────────────────────────────

  function addZoneDraft() {
    setZoneDrafts((d) => [...d, { name: '' }]);
  }

  function removeZoneDraft(idx: number) {
    setZoneDrafts((d) => d.filter((_, i) => i !== idx));
  }

  function updateZoneDraft(idx: number, name: string) {
    setZoneDrafts((d) => d.map((z, i) => (i === idx ? { name } : z)));
  }

  // Split selector — generates a starter set of zones for common
  // subdivisions of a location. 1 = whole place (no zones), 1/2 = two
  // halves, 1/4 = four quarters, 1/8 = eight eighths. The user is free to
  // rename or remove individual zones afterwards.
  //
  // Default names follow the screenshot pattern the user already uses for
  // 1/4 ("1A, 1B, 2A, 2B"). 1/8 extends that with C and D suffixes.
  type SplitOption = 1 | 2 | 4 | 8;
  function defaultZoneNamesForSplit(option: SplitOption): string[] {
    switch (option) {
      case 1:
        return [];
      case 2:
        return ['A', 'B'];
      case 4:
        return ['1A', '1B', '2A', '2B'];
      case 8:
        return ['1A', '1B', '1C', '1D', '2A', '2B', '2C', '2D'];
    }
  }

  const [splitWorking, setSplitWorking] = useState(false);

  async function applySplit(option: SplitOption) {
    const names = defaultZoneNamesForSplit(option);

    if (mode === 'create') {
      const existing = zoneDrafts.length;
      if (existing > 0) {
        const ok = await confirm({
          title: t('locations.zones.regenerateConfirmTitle'),
          body: t('locations.zones.regenerateConfirmBody', { count: existing }),
          confirmLabel: t('locations.zones.regenerateConfirmCta'),
          tone: 'destructive',
        });
        if (!ok) return;
      }
      setZoneDrafts(names.map((name) => ({ name })));
      return;
    }

    // Edit mode — replace existing zones via sequential API calls. The
    // simpler approach (instead of a dedicated bulk endpoint) is fine at
    // this scale (max 8 zones).
    if (zones.length > 0) {
      const ok = await confirm({
        title: t('locations.zones.regenerateConfirmTitle'),
        body: t('locations.zones.regenerateConfirmBody', { count: zones.length }),
        confirmLabel: t('locations.zones.regenerateConfirmCta'),
        tone: 'destructive',
      });
      if (!ok) return;
    }

    setSplitWorking(true);
    try {
      // Delete sequentially first — if a zone is referenced elsewhere
      // (zone_in_use), surface the error and stop so the user can resolve
      // manually before continuing.
      for (const z of zones) {
        await deleteZoneMut.mutateAsync(z.id);
      }
      for (let i = 0; i < names.length; i++) {
        await addZoneMut.mutateAsync({ name: names[i]!, sort_order: i });
      }
    } finally {
      setSplitWorking(false);
    }
  }

  // Ref into the availability fieldset so the modal's single Save button
  // commits buffered opening-hours edits too. The fieldset stays in
  // charge of its own dirty tracking + API call; we only trigger it from
  // here after the location-level save lands.
  const availabilityRef = useRef<LocationAvailabilityFieldsetHandle>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (!isHexValid) return;
    const cleanZones = zoneDrafts
      .map((z, i) => ({ name: z.name.trim(), sort_order: i }))
      .filter((z) => z.name.length > 0);
    await onSubmit({
      name: name.trim(),
      color: colorOverride.trim().toLowerCase(),
      notes: notes.trim(),
      place,
      zones: cleanZones,
    });
    // Edit mode only: opening hours sit in a child fieldset whose state
    // we don't lift up. After the location-level save resolves, ask the
    // fieldset to commit anything dirty. No-op when there are no edits.
    if (mode === 'edit') {
      await availabilityRef.current?.save();
    }
  }

  // Uses Radix Dialog so Radix treats this as a layer stacked above the
  // Sheet. react-remove-scroll then scopes pointer-events correctly across
  // both layers — no need for manual portal or capture-phase hacks.
  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[60] bg-foreground/30"
          onClick={onCancel}
        />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => { e.preventDefault(); onCancel(); }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[61] flex max-h-[90vh] w-[min(92vw,40rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <header className="flex items-center justify-between border-b border-border px-6 py-4">
              <DialogPrimitive.Title className="font-heading text-lg font-bold">
                {mode === 'create' ? t('locations.newLocation') : t('locations.editLocation')}
              </DialogPrimitive.Title>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('common.cancel')}
                onClick={onCancel}
              >
                <X weight="bold" />
              </Button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <label className="block">
                <span className="font-heading text-sm font-semibold">{t('locations.form.name')}</span>
                <Input
                  required
                  autoFocus
                  className="mt-1"
                  placeholder={t('locations.form.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <div className="mt-4">
                <span className="font-heading text-sm font-semibold">{t('locations.form.address')}</span>
                {place && !manualEntry ? (
                  <div className="mt-1 flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{place.formatted_address}</p>
                      {place.latitude !== 0 && place.longitude !== 0 ? (
                        <p
                          className="mt-0.5 text-xs text-foreground/60"
                          aria-label={t('locations.form.address')}
                        >
                          {[place.latitude.toFixed(5), place.longitude.toFixed(5)].join(', ')}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPlace(null);
                        setManualEntry(false);
                      }}
                    >
                      {t('locations.form.changeAddress')}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1">
                    <AddressAutocomplete
                      regionBias={i18n.resolvedLanguage?.toUpperCase().slice(0, 2) ?? ''}
                      language={i18n.resolvedLanguage ?? 'en'}
                      kinds="any"
                      hint={t('locations.form.addressHint')}
                      onPicked={(p) => {
                        setPlace(p);
                        setManualEntry(false);
                      }}
                      onLocated={(p) => {
                        setPlace(p);
                        setManualEntry(true);
                      }}
                      onUnavailable={() => setManualEntry(true)}
                      onClear={() => setPlace(null)}
                    />
                  </div>
                )}
              </div>

              {/* Zone management — create mode uses local drafts (submitted with the
                  form); edit mode uses live per-zone API calls immediately. */}
              <fieldset className="mt-6 rounded-xl border border-border p-4">
                <legend className="px-2 font-heading text-sm font-semibold">
                  {t('locations.zones.heading')}
                </legend>
                <p className="text-xs text-foreground/70">{t('locations.zones.tagline')}</p>

                {/* Split selector — bulk-generate a starter set of zones at
                    common subdivisions of the location (whole, half, quarter,
                    eighth). The user can rename or remove afterwards. The
                    currently-active split (inferred from the zone count) is
                    highlighted so the user sees the location's current state
                    at a glance. Only the canonical counts {0, 2, 4, 8} match —
                    a custom count (e.g. 3) leaves every pill unselected. */}
                {(() => {
                  const zoneCount = mode === 'create' ? zoneDrafts.length : zones.length;
                  const activeSplit: 1 | 2 | 4 | 8 | null =
                    zoneCount === 0 ? 1
                      : zoneCount === 2 ? 2
                        : zoneCount === 4 ? 4
                          : zoneCount === 8 ? 8
                            : null;
                  return (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-foreground/80">
                        {t('locations.zones.split.label')}
                      </span>
                      {([1, 2, 4, 8] as const).map((opt) => {
                        const active = opt === activeSplit;
                        return (
                          <button
                            key={opt}
                            type="button"
                            aria-pressed={active}
                            disabled={splitWorking || submitting || deleting}
                            onClick={() => { void applySplit(opt); }}
                            className={
                              active
                                ? 'rounded-pill border border-lime bg-lime px-3 py-1 text-xs font-medium text-ink transition-colors disabled:cursor-not-allowed disabled:opacity-50'
                                : 'rounded-pill border border-border bg-background px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50'
                            }
                          >
                            {opt === 1 ? '1' : `1/${opt}`}
                          </button>
                        );
                      })}
                      {splitWorking && (
                        <Spinner weight="bold" className="h-4 w-4 animate-spin text-foreground/50" />
                      )}
                    </div>
                  );
                })()}

                {mode === 'create' ? (
                  <>
                    {zoneDrafts.length === 0 ? (
                      <p className="mt-3 text-xs text-foreground/60">{t('locations.zones.empty')}</p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {zoneDrafts.map((z, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <Input
                              value={z.name}
                              placeholder={t('locations.zones.namePlaceholder')}
                              onChange={(e) => updateZoneDraft(idx, e.target.value)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t('locations.zones.remove')}
                              onClick={() => removeZoneDraft(idx)}
                            >
                              <Trash weight="bold" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={addZoneDraft}
                    >
                      <Plus weight="bold" />
                      {t('locations.zones.add')}
                    </Button>
                  </>
                ) : (
                  /* Edit mode — live mutations per zone */
                  <>
                    {zones.length === 0 ? (
                      <p className="mt-3 text-xs text-foreground/60">{t('locations.zones.empty')}</p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {zones.map((zone) => (
                          <li key={zone.id} className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Input
                                  ref={(el) => { zoneInputRefs.current[zone.id] = el; }}
                                  value={zoneNames[zone.id] ?? zone.name}
                                  placeholder={t('locations.zones.namePlaceholder')}
                                  disabled={pendingZone === zone.id}
                                  onChange={(e) =>
                                    setZoneNames((prev) => ({ ...prev, [zone.id]: e.target.value }))
                                  }
                                  onBlur={() => handleZoneBlur(zone)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      (e.target as HTMLInputElement).blur();
                                    }
                                  }}
                                />
                                {pendingZone === zone.id && (
                                  <Spinner
                                    weight="bold"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-foreground/40"
                                  />
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={t('locations.zones.remove')}
                                disabled={pendingZone === zone.id}
                                onClick={() => handleZoneDelete(zone)}
                              >
                                <Trash weight="bold" />
                              </Button>
                            </div>
                            {zoneErrors[zone.id] ? (
                              <p className="text-xs text-destructive">{zoneErrors[zone.id]}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      disabled={addZoneMut.isPending}
                      onClick={handleAddZone}
                    >
                      {addZoneMut.isPending ? (
                        <Spinner weight="bold" className="animate-spin" />
                      ) : (
                        <Plus weight="bold" />
                      )}
                      {t('locations.zones.add')}
                    </Button>
                  </>
                )}
              </fieldset>

              <fieldset className="mt-6 flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
                <legend className="px-1 text-sm font-medium text-foreground">
                  {t('locations.form.color')}
                </legend>
                <p className="text-xs text-muted-foreground">
                  {colorOverride
                    ? t('locations.form.colorOverridden')
                    : t('locations.form.colorAuto')}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colorOverride || palette.hex}
                    onChange={(e) => setColorOverride(e.target.value.toLowerCase())}
                    aria-label={t('locations.form.colorPicker')}
                    className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
                  />
                  <Input
                    type="text"
                    value={colorOverride}
                    onChange={(e) => setColorOverride(e.target.value.toLowerCase())}
                    placeholder={palette.hex}
                    maxLength={7}
                    spellCheck={false}
                    className="flex-1 font-mono"
                    aria-invalid={!isHexValid || undefined}
                  />
                  {colorOverride && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setColorOverride('')}
                      title={t('locations.form.colorReset')}
                    >
                      <ArrowsClockwise className="h-4 w-4" />
                      {t('locations.form.colorReset')}
                    </Button>
                  )}
                </div>
                {!isHexValid && (
                  <p className="text-xs text-destructive">{t('locations.form.colorInvalid')}</p>
                )}
              </fieldset>

              <label className="mt-4 block">
                <span className="font-heading text-sm font-semibold">
                  {t('locations.form.notes')}
                </span>
                <textarea
                  className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm focus:border-primary focus:outline-none"
                  rows={3}
                  placeholder={t('locations.form.notesPlaceholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>

              {/* Availability editor — only meaningful once a location exists
                  (rules and exceptions are children of an id). On the create
                  flow we skip it; users save the location first, then can
                  re-open it in edit mode to set hours. */}
              {mode === 'edit' && initial && slug ? (
                <LocationAvailabilityFieldset
                  ref={availabilityRef}
                  slug={slug}
                  token={token ?? null}
                  locationId={initial.id}
                />
              ) : null}

              {error ? (
                <p role="alert" className="mt-4 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
            </div>

            {/* Cancel button is intentionally omitted — the header's X icon
                already provides the dismissal affordance, and on mobile a
                third button forced the row to wrap. The destructive button
                is compacted to "Delete" + trash icon for the same reason. */}
            <footer className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
              <div>
                {mode === 'edit' && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={onDelete}
                    disabled={submitting || deleting}
                  >
                    <Trash weight="bold" />
                    {deleting ? t('locations.form.deleting') : t('common.delete')}
                  </Button>
                )}
              </div>
              <Button type="submit" disabled={submitting || deleting}>
                {submitting ? t('locations.form.saving') : t('locations.form.save')}
              </Button>
            </footer>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
